/**
 * formatApiError — normalise a FastAPI/axios error into a display string.
 *
 * FastAPI can return `detail` as:
 *   • a plain string ("Invalid client")
 *   • an object    ({code, message, ...})       — role_blocked_for_client, etc.
 *   • a Pydantic validation array               — 422 responses
 *   • undefined                                 — network / CORS / timeout
 *
 * A toast that renders `{code: "..."}` as [object Object] hides the real
 * reason a request failed. This helper always returns a human-readable
 * string suitable for `toast.error(...)`.
 *
 * Usage:
 *   } catch (e) {
 *     toast.error(formatApiError(e, 'Failed to create shift'));
 *   }
 */
export function formatApiError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail;

  // 1) Plain string — the common path.
  if (typeof detail === 'string' && detail.trim()) return detail;

  // 2) Pydantic 422 validation array: [{loc, msg, type, ...}, ...]
  if (Array.isArray(detail) && detail.length) {
    return detail
      .map((d) => {
        const loc = Array.isArray(d?.loc) ? d.loc.filter(Boolean).join('.') : '';
        return loc ? `${loc}: ${d?.msg || 'invalid'}` : (d?.msg || 'invalid');
      })
      .slice(0, 3)
      .join(' · ');
  }

  // 3) Structured object detail: {code, message, ...}
  if (detail && typeof detail === 'object') {
    return (
      detail.message ||
      detail.msg ||
      detail.error ||
      detail.code ||
      JSON.stringify(detail)
    );
  }

  // 4) Non-detail fields (older middleware writes to `.data.message`)
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object' && data.message) return data.message;

  // 5) Axios-level failures (network, CORS, timeout).
  if (err?.message) {
    // Add HTTP status if we have it so admins can grep logs.
    const status = err?.response?.status;
    return status ? `${err.message} (HTTP ${status})` : err.message;
  }

  return fallback;
}
